import { Component } from '@mycolorway/tao';
import comparators from './comparators';

export default Component('tr-table-view', {

  properties: {
    columns: {
      type: Object,
      default: {},
    },
    data: {
      type: Array,
      default: [],
    },
  },

  configs: {},

  connected() {
    this.initHandsontable();
  },

  disconnected() {
    if (this.handsontable) this.handsontable.destroy();
  },

  initHandsontable() {
    import('./handsontable').then(({ default: Handsontable, defaultConfigs }) => {
      this.Handsontable = Handsontable;
      this.beforeInitHandsontable();


      const configs = Object.assign({
        columns: this.getColumns(),
        colHeaders: this.renderColHeader.bind(this),
        colWidths: this.getColWidths(),
        data: this.data,
        hiddenColumns: this.getHiddenColumns(),
        mergeCells: this.getMergeCells(),
        cells(...args) {
          const handsontable = this.instance;
          return handsontable.rootElement.getCells(handsontable, ...args);
        },
      }, defaultConfigs, this.configs);

      this.handsontable = new Handsontable(this, configs);

      if (!configs.autoColumnSize) {
        this.handsontable.getPlugin('autoColumnSize').disablePlugin();
      }

      this.addCommonHooks();
      this.afterInitHandsontable();
      this.namespacedTrigger('handsontableInit');
    });
  },

  beforeInitHandsontable() {},
  afterInitHandsontable() {},

  addCommonHooks() {
    this.handsontable.addHook('afterOnCellMouseOver', this.handleCellMouseOver.bind(this));
    this.handsontable.addHook('beforeColumnResize', this.handleBeforeColumnResize.bind(this));
    this.handsontable.addHook('beforeRenderer', this.handleBeforeRenderer.bind(this));
    this.handsontable.addHook('beforeChangeRender', this.handleBeforeChangeRender.bind(this));
    this.handsontable.addHook('afterBeginEditing', this.handleAfterBeginEditing.bind(this));
    this.handsontable.addHook('beforeCopy', this.handleBeforeCopy.bind(this));
  },

  beforeGroupCellRender(group) {
    return group;
  },

  getCells(handsontable, row) {
    const rowData = handsontable.getSourceDataAtRow(row);

    if (rowData && rowData.group) {
      return {
        renderer: 'group',
        editor: false,
        beforeRender: this.beforeGroupCellRender.bind(this),
      };
    }

    return undefined;
  },

  getMergeCells() {
    const colCount = this.columns.length;
    const mergeCells = [];

    this.data.forEach((data, index) => {
      if (data.group) {
        mergeCells.push({
          row: index,
          col: 0,
          rowspan: 1,
          colspan: colCount,
        });
      }
    });

    return mergeCells;
  },

  handleCellMouseOver(event, { row, col }) {
    const hasPrevHoverRow = this.prevHoverRow !== null && this.prevHoverRow !== undefined;

    if (col >= 0 && (row >= 0 || hasPrevHoverRow)) {
      const colCount = this.handsontable.countCols();
      // eslint-disable-next-line no-plusplus
      for (let index = 0; index < colCount; index++) {
        if (row >= 0) {
          const cell = this.handsontable.getCell(row, index, true);
          if (cell) cell.classList.add('hover');
        }

        if (hasPrevHoverRow && this.prevHoverRow !== row) {
          const prevHoverCell = this.handsontable.getCell(this.prevHoverRow, index, true);
          if (prevHoverCell) prevHoverCell.classList.remove('hover');
        }
      }

      this.prevHoverRow = row >= 0 ? row : null;
    }
  },

  handleBeforeColumnResize(col, newSize) {
    if (newSize < 100) {
      this.handsontable.getPlugin('manualColumnResize').setManualSize(col, 100);
    }
  },

  handleBeforeRenderer(td, row, col, prop, value, cellProperties) {
    /* eslint-disable no-param-reassign */
    if (cellProperties.required && !value) {
      cellProperties.valid = false;
      td.dataset.errorMessage = `${cellProperties.titleText}不能为空`;
    } else {
      td.dataset.errorMessage = cellProperties.errorMessage;
    }
  },

  handleBeforeChangeRender(changes) {
    changes.forEach(([row, prop]) => {
      const col = this.handsontable.propToCol(prop);
      this.handsontable.setCellMetaObject(row, col, {
        valid: true,
        errorMessage: null,
      });
    });
  },

  handleAfterBeginEditing(row, column) {
    const cells = [this.handsontable.getCell(row, column, true)];
    const { fixedColumnsLeft } = this.handsontable.getSettings();
    if (fixedColumnsLeft) cells.push(this.handsontable.getCell(row, column));

    cells.filter(cell => !!cell).forEach(cell => cell.classList.add('editing'));
  },

  handleBeforeCopy(data, coords) {
    const { columns } = this.handsontable.getSettings();

    coords.forEach(({ startCol, endCol }) => {
      const cols = [...Array(endCol - startCol + 1).keys()].map(col => col + startCol);
      data.forEach((rowData) => {
        rowData.forEach((cellData, index) => {
          const column = columns[cols[index]];

          if (column && column.renderer === 'select' && cellData) {
            cellData = JSON.parse(cellData);

            if (column.multiple) {
              rowData.splice(index, 1, cellData.map(item => item.text).join(', '));
            } else {
              rowData.splice(index, 1, cellData.text);
            }
          }
        });
      });
    });

    return true;
  },

  renderColHeader(colIndex) {
    const column = this.columns[colIndex];
    return `<i class="twr twr-${column.icon}"></i>${column.title}`;
  },

  getColumns() {
    const pickColumnOptions = ({ title, icon, ...columnOptions }) => columnOptions;
    return this.columns.map(column => ({
      titleText: column.title,
      columnSorting: this.getColumnSorting(column),
      ...pickColumnOptions(column),
    }));
  },

  getColumnSorting(column) {
    if (column.sortable) {
      const sortingConfig = {
        indicator: true,
        headerAction: true,
        sortEmptyCells: false,
        compareFunctionFactory: comparators[column.renderer] || comparators.default,
      };

      return sortingConfig;
    }

    return {
      indicator: false,
      headerAction: false,
      compareFunctionFactory() {
        return () => 0;
      },
    };
  },

  getColWidths() {
    return this.columns.map(column => (column.options || {}).width || 120);
  },

  getHiddenColumns() {
    const hiddenColumns = [];

    this.columns.forEach((column, index) => {
      if (column.hidden) hiddenColumns.push(index);
    });

    return hiddenColumns.length ? { columns: hiddenColumns } : false;
  },

  hideColumnsByProp(prop) {
    const hiddenColumnsPlugin = this.handsontable.getPlugin('hiddenColumns');
    if (!hiddenColumnsPlugin.enabled) hiddenColumnsPlugin.enablePlugin();

    const columns = (Array.isArray(prop) ? prop : [prop]).map(p => this.getColumnByProp(p));
    hiddenColumnsPlugin.hideColumns(columns);
    this.handsontable.render();
  },

  showColumnsByProp(prop) {
    const hiddenColumnsPlugin = this.handsontable.getPlugin('hiddenColumns');
    if (!hiddenColumnsPlugin.enabled) hiddenColumnsPlugin.enablePlugin();

    const columns = (Array.isArray(prop) ? prop : [prop]).map(p => this.getColumnByProp(p));
    hiddenColumnsPlugin.showColumns(columns);
    this.handsontable.render();
  },

  updateColumns(columns) {
    this.columns = columns;
    this.handsontable.updateSettings({
      columns: this.getColumns(),
      hiddenColumns: this.getHiddenColumns(),
      colWidths: this.getColWidths(),
    });
  },

  getColumnByProp(prop) {
    const { columns } = this.handsontable.getSettings();
    const columnIndex = columns.findIndex(column => column.name === prop);
    return this.handsontable.toVisualColumn(columnIndex);
  },
});
